// ColyseusTransport — implements NetTransport against a Colyseus GameRoom.
//
// Wire layout (must match server/src/rooms/GameRoom.ts):
//   - Schema GameState           — patches at ~60Hz (high-level state)
//   - Binary message type 1      — bones + velocities snapshot at 60Hz
//     (Float32Array: 13 floats per bone = pos+rot+linvel+angvel)
//   - "impact" / "footstep" / "swing" — JSON event messages
//   - "welcome" — sent once on join with { charId, tickHz, ... }
//
// Model (Phase 1 of NETCODE_EVOLUTION.md):
//
//   The CLIENT runs a single-character Rapier World at the fixed 60Hz tick.
//   That world contains the local character only — remote characters are not
//   in the client's Rapier sim; their bones arrive interpolated from server
//   snapshots and feed straight into the renderer each frame.
//
//   Every client tick:
//     1. Sync local Character scalar state (hp/ko/stun/etc.) from the latest
//        server snap so server-authoritative damage / KO always dominates.
//     2. Save the live input to an input ring buffer keyed by client tick.
//     3. World.step(dt, { localCharId -> input }) — the local player's
//        movement, gait, jump, swing, and grab animations all advance on
//        the client immediately. Nothing is offset-faked any more.
//     4. Save the local Ragdoll's full state (pos+rot+linvel+angvel per
//        bone) into predictedBonesBuffer[clientTick].
//
//   Every server schema patch carries `slot.lastProcessedInputTick` (the
//   client tick whose input produced the server's new pose). We compare
//   predictedBonesBuffer[ackTick] against the server pose:
//     - If pelvis drift < RECONCILE_POSITION_THRESHOLD  → no-op, prediction
//       held; local player is running their own sim with 0ms perceived lag.
//     - If drift exceeds the threshold → snap the client Ragdoll to the
//       server's current bones (including velocities — U25 / tip U24).
//       Phase 1.5 will replay un-acked inputs after the snap; Phase 1
//       accepts the occasional short rubber-band.
//
//   Remote characters continue to be rendered from the existing
//   interpolating snapshot pipeline (bracketed server snapshots + adaptive
//   jitter buffer). That path was already solid.
//
//   Camera yaw + pitch still come directly from Input each frame, so head-
//   look has zero perceived latency regardless of server auth.

import type { Room } from '@colyseus/sdk';
import { CONFIG } from '../config';
import { Input } from '../core/Input';
import { emptyInput, InputBtn, type InputFrame } from '../core/InputFrame';
import { World } from '../sim/World';
import { MatchState, type TeamMember, type CrownCounts } from '../sim/MatchState';
import {
  emptySnapshot,
  type CharacterSnapshot,
  type FootstepEvent,
  type ImpactEvent,
  type SimState,
  type SplashEvent,
  type SwingStartEvent,
  type TeamId
} from '../sim/SimState';
import type { NetTransport } from './NetTransport';
import type { AIDifficulty } from '../sim/ai/AIController';
import type { HatId } from '../render/HatModels';
import type { Character, CharacterInternalState } from '../sim/Character';
import { makeEmptyInternalState } from '../sim/Character';
import { netcodeProfiler } from '../profile/NetcodeProfiler';
import type { ClientReconcileEvent, ServerProfile } from '../profile/types';

interface ProfileDumpMessage {
  sessionId: string;
  durationMs: number;
  requesterCharId: number;
  tickHz: number;
  mapId: string;
  serverStartAtMs: number;
  profile: ServerProfile;
}

// -- Schema types we read from room.state.
interface PlayerSlotState {
  charId: number;
  sessionId: string;
  isAI: boolean;
  connected: boolean;
  name: string;
  color: string;
  hatId: string;
  team: number;
  hp: number;
  ko: number;
  koStunRemaining: number;
  wasted: boolean;
  ringedOut: boolean;
  knockedOut: boolean;
  stamina: number;
  staminaLocked: boolean;
  stunRemaining: number;
  facingYaw: number;
  pelvisX: number;
  pelvisY: number;
  pelvisZ: number;
  swingPhaseL: number;
  swingPhaseR: number;
  grabbing: boolean;
  doubleHolding: boolean;
  beingGrabbed: boolean;
  wriggle: number;
  bothAir: boolean;
  isSwimming: boolean;
  ping: number;
  lastProcessedInputTick: number;
}
interface GameStateLike {
  mapId: string;
  tick: number;
  phase: string;
  round: number;
  crownsRed: number;
  crownsBlue: number;
  lastRoundWinner: string;
  players: PlayerSlotState[];
  /** False until every expected human has joined the room. While false the
   *  client must not advance its own World — otherwise a fast-loading
   *  player would start simulating before the other side's browser had
   *  even finished bundle preload. */
  matchStarted: boolean;
  /** Dynamic-map-element scalars replicated from the server each tick. */
  shipRoll: number;
  shipYaw: number;
  /** Sinking-ship deck Y, replicated each tick. Stays at 0 on other maps. */
  shipSinkY: number;
  /** Match-scoped PRNG seed for dynamic behaviors (asteroid field). */
  mapSeed: number;
}

const TICK_HZ = CONFIG.sim.tickHz;
const BUFFER_LEN = 256;
// Wire layout per bone in the binary "bones" broadcast:
//   [px, py, pz, qx, qy, qz, qw, lvx, lvy, lvz, avx, avy, avz]
const FLOATS_PER_BONE_WIRE = 13;
// Subset used for rendering (pos + rot). Remote-character interp only
// touches these 7 floats; the remaining 6 (velocities) are reserved for
// local-char rollback.
const FLOATS_PER_BONE_RENDER = 7;
// How far behind the newest server snapshot we render REMOTE characters.
// Adaptive — see `interpDelayMs` / `updateInterpDelay()`. On LAN this sits
// at INTERP_DELAY_MIN_MS (stddev ≈ 0); on a jittery connection it widens
// so we always have a snapshot ahead of renderTime to interpolate toward.
// The LOCAL character ignores this — they run their own sim.
// On LAN with <1ms ping the jitter stddev is ~0, so the adaptive target
// bottoms out at this floor. Lower = lower end-to-end latency, but less
// headroom for a one-off late packet before the bracket extrapolates.
// 2ms is essentially "render at the newest snap minus a whisker".
const INTERP_DELAY_MIN_MS = 2;
const INTERP_DELAY_MAX_MS = 120;
const INTERP_JITTER_WINDOW = 30;
const INTERP_JITTER_MULTIPLIER = 3;
const INTERP_ATTACK = 0.4;
const INTERP_RELEASE = 0.02;
// Cap on how far behind `targetTime` the render clock is allowed to sit. Any
// gap bigger than this is caught up (hard snap OR gradual, see tick()). At
// 500 (the old value) a permanent ~370ms lag accumulated during the
// interpDelay decay transient: target pulls forward extra as interpDelay
// shrinks from its attack peak (120ms) toward steady-state (~10ms), and
// since renderTimeMs only advances at wall rate, the gap sits forever. 50ms
// is generous vs. actual rAF jitter on LAN but tight vs. the behaviour we
// care about (input → remote visible).
const MAX_INTERP_LAG_MS = 50;

// Flip on with `VITE_NETCODE_TEL=1` (e.g. in .env.local) to surface per-second
// client-side latency summaries in the browser console. Off by default —
// zero runtime cost when unset since the tel blocks are gated at each site.
const NETCODE_TEL = ((import.meta.env as unknown as { VITE_NETCODE_TEL?: string }).VITE_NETCODE_TEL) === '1';
const SNAP_BUFFER_LEN = 256;
// Ring buffer sizes for the local-char prediction record. 256 ticks at 60Hz
// ≈ 4.3s of history — comfortably more than any realistic RTT + jitter
// window, and zero-GC because the Float32Arrays are allocated once.
const PRED_BUFFER_LEN = 256;
// How many most-recent InputFrames we bundle into every outbound `input`
// message. 6 at 60Hz ≈ 100ms of redundancy, so up to ~5 consecutive
// dropped packets still don't cost a tick (the 6th redundant copy would
// only be missed on catastrophic loss).
const SENT_INPUT_HISTORY_LEN = 6;
// Rollback trigger. Anything under this is considered "prediction held" and
// leaves the local sim untouched. Set just above typical Rapier float
// drift so we don't rollback on floating-point noise, but below any
// meaningful gameplay divergence (a single 60Hz tick of movement is
// ~4cm at walk speed, 6.5cm at sprint — so 8cm triggers only on real
// disagreement, not on one-tick staleness).
const RECONCILE_POSITION_THRESHOLD = 0.08;   // metres
// Phase 2.6: while a character is currently grab-coupled (grabber OR
// victim, either side of the wire), independent per-body snaps fight the
// active Rapier spherical joints and launch limbs. Below this pelvis
// drift we TOLERATE the divergence — the grab itself bounds how far the
// pair can drift apart because the grabber IS simulated locally from
// forwarded inputs. Above it we release every joint touching the char
// first, then snap as normal.
const GRAB_COUPLED_HARD_SNAP_THRESHOLD = 1.5;     // metres
const GRAB_COUPLED_HARD_SNAP_THRESHOLD_SQ =
  GRAB_COUPLED_HARD_SNAP_THRESHOLD * GRAB_COUPLED_HARD_SNAP_THRESHOLD;
// After this many ticks out-of-date (ackTick way behind current), assume
// the buffer wrapped / severe stall and do an unconditional hard snap.
const HARD_SNAP_AGE_TICKS = PRED_BUFFER_LEN - 16;

// Idle keepalive injection. When a human stops sending real input the
// character settles into a pose where Rapier's auto-sleep + FP solver drift
// can produce a visible twitch (see plan: expressive-soaring-eagle.md). We
// fire a tiny "keepalive" frame periodically — a non-zero moveZ and the
// Keepalive flag — that flows through the normal sim path on both peers,
// keeping bodies awake and giving rollback a deterministic non-zero input
// to replay. The flag is sim-inert (no branch in Character.ts reads it).
const KEEPALIVE_IDLE_THRESHOLD_MS = 5000;
const KEEPALIVE_INTERVAL_MS = 5000;
// Burst window after each round transitions to 'playing' — physics is
// still settling from forceReset so we lean on keepalives more aggressively.
const ROUND_START_KEEPALIVE_WINDOW_MS = 1500;
const ROUND_START_KEEPALIVE_INTERVAL_MS = 250;
// Small enough to be visually invisible (well below any movement-input
// dead-zone the character cares about) but non-zero so Rapier sees a
// fresh impulse and doesn't sleep the body.
const KEEPALIVE_MOVE_Z = 0.02;

interface ServerSnapshot {
  /** Server tick this snapshot represents. */
  tick: number;
  /** Wall-clock time we received the patch (ms). Drives interpolation. */
  receivedAtMs: number;
  /** charId → high-level fields. */
  players: Map<number, PlayerSlotState>;
  phase: string;
  round: number;
  crowns: CrownCounts;
  lastRoundWinner: TeamId | null;
  /** Replicated dynamic-map scalars. Bracket-lerped by renderTimeMs on
   *  tick() and pushed into the tilt behavior just before the local
   *  World steps, so Rapier's kinematic ship collider carries the
   *  server's rotation through the local character's sim. */
  shipRoll: number;
  shipYaw: number;
  /** Replicated deck height for the sinking-ship map. Pushed into the
   *  sinkingShip behavior each tick on followers. */
  shipSinkY: number;
}

interface BonesSnapshot {
  tick: number;
  receivedAtMs: number;
  /** Per-char Float32Array of `boneCount * FLOATS_PER_BONE_WIRE` floats. */
  perChar: Map<number, Float32Array>;
}

export interface ColyseusTransportOptions {
  room: Room<GameStateLike>;
  /** Slot id (0..3) the local human owns. Sent by the server in "welcome". */
  localCharId: number;
  /** Bundle id the server is running. */
  mapId: string;
}

export class ColyseusTransport implements NetTransport {
  readonly room: Room<GameStateLike>;
  readonly world: World;
  readonly match: MatchState;
  readonly localCharId: number;

  private input: Input;
  private boneCount: number;
  private prev: SimState | null = null;
  private latest: SimState | null = null;
  private buffer: Array<SimState | null> = new Array(BUFFER_LEN).fill(null);
  private tickCounter = 0;

  // Server snapshot ring buffers (newest at end).
  private snapBuffer: ServerSnapshot[] = [];
  private bonesBuffer: BonesSnapshot[] = [];
  private renderTimeMs = 0;
  private renderTimeInitialized = false;
  /** Per-rAF scratch holding bracket-interpolated render-format bones (7
   *  floats/bone: pos+quat) for every non-local character. Written by
   *  renderTick() at rAF cadence; the renderer reads it via remoteBonesAt()
   *  to apply the freshest possible pose to remote meshes each frame. This
   *  is the path that gives 120Hz visual motion to remotes — the alpha-lerp
   *  in CharacterView only ever sees 60Hz-spaced inputs from the synth, so
   *  on its own it can't produce sub-step motion. */
  private _remoteBoneScratch: Map<number, Float32Array> = new Map();

  // Pending events drained per synth.
  private pendingImpacts: ImpactEvent[] = [];
  private pendingFootsteps: FootstepEvent[] = [];
  private pendingSwings: SwingStartEvent[] = [];
  private pendingSplashes: SplashEvent[] = [];

  // Spectate-focus bookkeeping. Same as LocalTransport.
  private prevMatchPhase: 'playing' | 'roundOver' | 'gameOver' = 'playing';

  // Keepalive injection bookkeeping (see KEEPALIVE_* constants above).
  // -1 sentinel = "never" so the very first frame doesn't trip an idle
  // threshold. roundPlayingSinceMs is set when phase transitions to
  // 'playing' (covers both round transitions and the first round of a
  // match) and is what gates the post-reset burst window.
  private lastRealInputAtMs = -1;
  private lastKeepaliveAtMs = -1;
  private roundPlayingSinceMs = -1;

  // The local Character instance inside `this.world`. We step this character
  // each tick with local input; its Ragdoll is the source of truth for the
  // rendered local body.
  private localCharacter: Character | null = null;
  /** Handle to the ship-tilt behavior in the local arena, if the current
   *  map declares one. We cache the lookup once (behaviorDefsById is a
   *  Map; resolving every tick isn't expensive, but caching makes the
   *  dependency explicit). Also duck-typed via `setReplicatedTransform`
   *  to avoid importing the concrete SpaceshipTiltBehavior symbol into
   *  the transport (keeps the network layer decoupled from map
   *  internals). */
  private tiltBehavior: {
    setReplicatedTransform(roll: number, yaw: number): void;
  } | null = null;
  /** Handle to the sinking-ship behavior in the local arena, if the
   *  current map declares one. Same duck-typing reasoning as
   *  `tiltBehavior` above. */
  private sinkingShipBehavior: {
    setReplicatedY(y: number): void;
  } | null = null;
  /** Characters present in the local predicted World. In the stabilized
   *  online model this is the LOCAL character only — remotes render from
   *  server snapshots so they never perturb the local Rapier solver. */
  private charactersById: Map<number, Character> = new Map();

  // Local-char prediction ring buffers (input history + per-tick bones +
  // per-tick scalar state). `predictedTickBuffer[i]` is the shared tick
  // stamp — same ring index serves all characters because we save every
  // character's state on the same ring slot each tick.
  private inputBuffer: Array<InputFrame | null> = new Array(PRED_BUFFER_LEN).fill(null);
  private predictedBonesRings: Map<number, Array<Float32Array | null>> = new Map();
  private predictedStateRings: Map<number, Array<CharacterInternalState | null>> = new Map();
  private predictedTickBuffer: Int32Array = new Int32Array(PRED_BUFFER_LEN).fill(-1);
  /** Set once we've received enough server state to seed every character's
   *  baseline. Before that, reconciliation is a straight snap-all. */
  private reconcileInitialized = false;
  /** Scratch map reused in both the steady-state tick and the replay loop
   *  to avoid per-tick allocation. */
  private _stepInputsScratch: Map<number, InputFrame> = new Map();

  // Phase 2.5 per-remote input queues. Server broadcasts `forward-inputs`
  // each tick; we insert frames here keyed by server tick, then on each
  // client tick consume the oldest available frame for each remote — same
  // pattern as the server's Phase 2 dequeue.
  //
  // Keyed by charId; values are Map<serverTick, InputFrame>. We skip the
  // local char: our own input is driven directly from `liveInputFrame`,
  // the server's forward of our input is redundant.
  private remoteInputQueues: Map<number, Map<number, InputFrame>> = new Map();
  private remoteInputHead: Map<number, number> = new Map();
  private remoteLastConsumed: Map<number, InputFrame> = new Map();

  // Most recent input frame polled + sent from pollAndSendLiveInput(). The
  // fixed-step tick() reads from this instead of polling Input directly so
  // Input.poll()'s gamepad-yaw side effects only fire once per rAF (not
  // doubled up with the fixed-step substep count). Null only before the
  // first render frame has run.
  private liveInputFrame: InputFrame | null = null;

  // Phase 2.6 scratch set reused across onSchemaPatch calls. Holds the
  // union of client-side + server-side grab-coupled charIds so the snap
  // paths can skip (or tolerate) divergence for bodies whose spherical
  // joints would otherwise explode under per-body teleports.
  private _grabCoupledScratch: Set<number> = new Set();

  // Phase 2: rolling bundle of recently-sent InputFrames. We send the last
  // N (SENT_INPUT_HISTORY_LEN) with every rAF, so a single dropped UDP-ish
  // WebSocket packet doesn't cost the server a tick's worth of input — the
  // next packet carries the same frames and the server's tick-ordered
  // queue de-duplicates by tick. Dedup-on-push: if the rAF cadence is
  // higher than the sim tick rate, multiple polls can share a tick; only
  // the newest per tick is kept (the older same-tick entry is overwritten
  // in place so the history stays at monotonically increasing ticks).
  private sentInputHistory: InputFrame[] = [];

  private knownBoneCount = 9;

  // Adaptive interp buffer — recomputed on each schema patch from the
  // stddev of recent inter-arrival times. See INTERP_* constants.
  private interpDelayMs: number = INTERP_DELAY_MIN_MS;
  private interarrivalSamples: number[] = [];
  private lastPatchReceivedAtMs: number = -1;

  // Diagnostics — read by an optional debug HUD (Phase 0 instrumentation).
  rollbackCount = 0;
  lastReconcileDrift = 0;

  // -- Always-on latency telemetry (issue: "250ms remote action delay").
  // Rolling window per ~60 ticks (1 sec). All values are in ms unless noted.
  private telAckLagSum = 0;      // client tickCounter - slot.lastProcessedInputTick, in ticks
  private telAckLagMax = 0;
  private telAckLagSamples = 0;
  private telSnapStaleSum = 0;   // wall_now - newestSnap.receivedAtMs at tick() time
  private telSnapStaleMax = 0;
  private telRenderLagSum = 0;   // wall_now - renderTimeMs at tick() time
  private telRenderLagMax = 0;
  private telInterArrivalSum = 0; // per-patch inter-arrival (ms)
  private telInterArrivalMax = 0;
  private telInterArrivalSamples = 0;
  private telTicksThisWindow = 0;
  private telLastWindowWallMs = 0;

  constructor(input: Input, opts: ColyseusTransportOptions) {
    this.room = opts.room;
    this.input = input;
    this.localCharId = opts.localCharId;
    this.boneCount = this.knownBoneCount;

    const teamMembers = readRosterFromState(opts.room.state, this.localCharId);
    this.match = new MatchState(teamMembers);

    // Stabilized online model: the client's predicted Rapier World contains
    // ONLY the local character. Remote characters render from the server's
    // snapshot/bones stream instead of sharing the same local solver.
    //
    // authoritative=false makes dynamic-map behaviors (ship tilt,
    // asteroid-field damage) switch to follower mode: state machines stop
    // running, damage is server-only, and the behaviors await replicated
    // updates from the transport. The seed override keeps our seeded-RNG
    // behaviors (rocks) in near-lockstep with the server until the
    // periodic binary correction from GameRoom lands.
    const mapSeed = opts.room.state.mapSeed >>> 0;
    this.world = new World({
      playerIds: [this.localCharId],
      mapId: opts.mapId,
      teamById: { [this.localCharId]: this.match.teamOf(this.localCharId) },
      authoritative: false,
      behaviorParamOverrides: {
        asteroids: { seed: mapSeed }
      }
    });
    for (const ch of this.world.characters) {
      this.charactersById.set(ch.id, ch);
      // Pre-allocate per-character ring buffers so the steady-state tick
      // never allocates.
      this.predictedBonesRings.set(ch.id, new Array(PRED_BUFFER_LEN).fill(null));
      this.predictedStateRings.set(ch.id, new Array(PRED_BUFFER_LEN).fill(null));
    }
    const localCh = this.charactersById.get(this.localCharId);
    if (!localCh) {
      throw new Error(`[ColyseusTransport] local character (id=${this.localCharId}) missing from world`);
    }
    this.localCharacter = localCh;

    // Cache dynamic-map behavior handles for per-tick replication. Only
    // populated when the current map declares them — other maps leave
    // these null and the tick/message paths short-circuit. Duck-typed so
    // the transport stays decoupled from behavior concrete types; if
    // behaviorDefsById returns something without the replication API we
    // treat it as missing.
    const tilt = this.world.arena.behaviorDefsById.get('tilt') as {
      setReplicatedTransform?: (roll: number, yaw: number) => void;
    } | undefined;
    if (tilt && typeof tilt.setReplicatedTransform === 'function') {
      this.tiltBehavior = tilt as { setReplicatedTransform(roll: number, yaw: number): void };
    }
    const sink = this.world.arena.behaviorDefsById.get('sink') as {
      setReplicatedY?: (y: number) => void;
    } | undefined;
    if (sink && typeof sink.setReplicatedY === 'function') {
      this.sinkingShipBehavior = sink as { setReplicatedY(y: number): void };
    }
    const asteroids = this.world.arena.behaviorDefsById.get('asteroids') as {
      applyReplicatedState?: (buf: ArrayBuffer) => void;
    } | undefined;
    const asteroidApply = asteroids && typeof asteroids.applyReplicatedState === 'function'
      ? asteroids.applyReplicatedState.bind(asteroids)
      : null;

    // Wire room callbacks.
    this.room.onStateChange((state) => this.onSchemaPatch(state));
    this.room.onMessage(1 as unknown as string, (raw: ArrayBuffer | Uint8Array) => {
      this.onBonesMessage(raw instanceof Uint8Array ? raw : new Uint8Array(raw));
    });
    // Dynamic-map binary correction (message type 2). Currently only the
    // asteroid field emits these. Opaque bytes → the behavior's own
    // applyReplicatedState unpacks into rock bodies.
    this.room.onMessage(2 as unknown as string, (raw: ArrayBuffer | Uint8Array) => {
      if (!asteroidApply) return;
      const ab = raw instanceof Uint8Array
        ? (raw.buffer.slice(raw.byteOffset, raw.byteOffset + raw.byteLength) as ArrayBuffer)
        : raw;
      asteroidApply(ab);
    });
    this.room.onMessage('impact', (msg: ImpactEvent[]) => {
      for (const ev of msg) this.pendingImpacts.push(ev);
      if (netcodeProfiler.isRecording()) {
        for (const ev of msg) {
          netcodeProfiler.recordImpact({
            fromCharId: ev.attackerId,
            toCharId: ev.victimId,
            dmg: ev.strength,
            posX: ev.x, posY: ev.y, posZ: ev.z
          });
        }
      }
    });
    this.room.onMessage('footstep', (msg: FootstepEvent[]) => {
      for (const ev of msg) this.pendingFootsteps.push(ev);
      if (netcodeProfiler.isRecording()) {
        for (const ev of msg) {
          netcodeProfiler.recordFootstep({
            charId: ev.characterId,
            foot: ev.side,
            posX: ev.x, posY: ev.y, posZ: ev.z
          });
        }
      }
    });
    this.room.onMessage('swing', (msg: SwingStartEvent[]) => {
      for (const ev of msg) this.pendingSwings.push(ev);
      if (netcodeProfiler.isRecording()) {
        for (const ev of msg) {
          netcodeProfiler.recordSwing({ charId: ev.characterId, side: ev.side });
        }
      }
    });
    this.room.onMessage('splash', (msg: SplashEvent[]) => {
      for (const ev of msg) this.pendingSplashes.push(ev);
    });
    // Phase 2.5 forwarded inputs — every other character's input for each
    // server tick, so we can step them locally.
    this.room.onMessage('forward-inputs', (msg: {
      tick: number;
      frames: Array<{ charId: number; moveX: number; moveZ: number; aimYaw: number; buttons: number }>;
    }) => {
      for (const f of msg.frames) {
        if (f.charId === this.localCharId) continue; // ignore our own echo
        let queue = this.remoteInputQueues.get(f.charId);
        if (!queue) {
          queue = new Map<number, InputFrame>();
          this.remoteInputQueues.set(f.charId, queue);
        }
        const head = this.remoteInputHead.get(f.charId);
        if (head !== undefined && msg.tick < head) continue; // stale
        queue.set(msg.tick, {
          tick: msg.tick,
          moveX: f.moveX,
          moveZ: f.moveZ,
          aimYaw: f.aimYaw,
          buttons: f.buttons
        });
        // Cap per-queue growth — worst case a flood of messages from a
        // server that's several seconds ahead of our consume rate.
        if (queue.size > 64) {
          const sorted = Array.from(queue.keys()).sort((a, b) => a - b);
          while (queue.size > 64) queue.delete(sorted.shift()!);
        }
      }
    });
    // RTT pong.
    this.room.onMessage('ping', (msg: { t: number }) => {
      try { this.room.send('pong', { t: msg.t }); } catch { /* noop */ }
    });
    // Netcode profiler responses from the server. The one we care about is
    // 'profile:dump', which carries the server profile half of a combined
    // export. `_pendingDumpResolve` is set by requestProfileDump() while
    // waiting; absent here means the dump arrived unsolicited (ignore).
    this.room.onMessage('profile:dump', (msg: ProfileDumpMessage) => {
      const resolve = this._pendingDumpResolve;
      this._pendingDumpResolve = null;
      if (resolve) resolve(msg);
    });
    this.room.onMessage('profile:error', (msg: { message: string }) => {
      const reject = this._pendingDumpReject;
      this._pendingDumpResolve = null;
      this._pendingDumpReject = null;
      if (reject) reject(new Error(msg.message));
    });
    this.room.onMessage('profile:started', (_msg: unknown) => {
      // No-op today; the client tracks its own start state.
    });

    // Capture the initial server state if it's already present.
    if ((opts.room.state as GameStateLike | undefined)?.tick !== undefined) {
      this.onSchemaPatch(opts.room.state);
    }
  }

  // -- NetTransport surface -------------------------------------------------

  tick(dt: number): void {
    // Hold the local sim while the server hasn't started the match yet —
    // i.e. we're waiting for the other human(s) to finish joining. The
    // server's GameState.matchStarted flips true once every expectingHuman
    // seat is filled; until then, stepping the world would desync us from
    // the frozen server, burn input ticks with no one to consume them,
    // and start footsteps / gait cycles before the match has begun.
    if (!this.room.state.matchStarted) return;
    const prof = netcodeProfiler.isRecording();
    const tickStartMs = prof ? performance.now() : 0;
    let syncEndMs = 0, stepEndMs = 0, captureEndMs = 0, synthEndMs = 0;
    let profRemoteInputs: Array<{ charId: number; depthBefore: number; source: -1 | 0 | 1; consumedTick: number; frame: InputFrame | null }> | null = null;

    const isRoundOver = this.latest?.roundOver ?? false;
    const frameRaw = this.liveInputFrame ?? emptyInput(this.tickCounter);
    // Stamp this tick's input with the CURRENT clientTick (the liveInputFrame
    // was tagged with whatever tick was current at rAF, which may lag or
    // lead this fixed-step tick by one). Using the fixed-step tick as the
    // authoritative key keeps input & prediction buffers aligned.
    const frame: InputFrame = {
      tick: this.tickCounter,
      moveX: frameRaw.moveX,
      moveZ: frameRaw.moveZ,
      aimYaw: frameRaw.aimYaw,
      buttons: frameRaw.buttons
    };

    // 1. Sync server-authoritative scalar state onto the local Character
    //    before stepping. Remotes render from server snapshots directly.
    const newestSnap = this.snapBuffer[this.snapBuffer.length - 1];
    const localCh = this.localCharacter;
    if (newestSnap && localCh) {
      const slot = newestSnap.players.get(this.localCharId);
      if (slot) this.syncLocalScalar(localCh, slot);
    }
    if (prof) syncEndMs = performance.now();

    // 2. Store this tick's local input.
    this.inputBuffer[this.tickCounter % PRED_BUFFER_LEN] = frame;

    // 2b. Push the latest replicated ship roll/yaw into the tilt behavior
    //     BEFORE stepping the world, so Rapier rotates the kinematic ship
    //     body along with whatever the server computed — the local
    //     character's ragdoll then rides the deck through the tilt. Uses
    //     the newest received snapshot (no bracket-lerp needed: the
    //     schema ships every tick at 60Hz and the ship rotates slowly).
    if (this.tiltBehavior && this.snapBuffer.length > 0) {
      const s = this.snapBuffer[this.snapBuffer.length - 1]!;
      this.tiltBehavior.setReplicatedTransform(s.shipRoll, s.shipYaw);
    }
    // Same channel for the sinking-ship deck height. The kinematic deck
    // body is local to each client (so the local character's ragdoll has
    // something to stand on), but its Y is purely server-driven.
    if (this.sinkingShipBehavior && this.snapBuffer.length > 0) {
      const s = this.snapBuffer[this.snapBuffer.length - 1]!;
      this.sinkingShipBehavior.setReplicatedY(s.shipSinkY);
    }

    // 3. Step the LOCAL predicted world only. Remote characters never touch
    //    the local solver in the stabilized online model.
    if (prof) profRemoteInputs = [];
    if (localCh) {
      const inputs = this._stepInputsScratch;
      inputs.clear();
      if (!isRoundOver) {
        inputs.set(this.localCharId, frame);
      }
      this.world.step(dt, inputs);
      if (prof) stepEndMs = performance.now();

      // 4. Capture predicted bones + scalar state for the local character.
      this.capturePredictedStateAll(this.tickCounter);
      if (prof) captureEndMs = performance.now();
    }

    // 5. renderTimeMs is now advanced per-rAF in renderTick(), not here.
    //    The synth below reads whatever value renderTick last wrote — at
    //    60Hz fixed step that's at most one rAF stale, which is fine for
    //    the scalar fields and KO-replay bone history this snapshot feeds.
    //    See renderTick() below.

    // 6. Synth the SimState for this render frame. Local char bones come
    //    from the client Ragdoll (just stepped); remotes come from interp.
    const synth = this.synthesizeAt(this.renderTimeMs);
    synth.tick = this.tickCounter;
    this.prev = this.latest;
    this.latest = synth;
    this.buffer[this.tickCounter % BUFFER_LEN] = synth;

    if (prof && localCh) {
      synthEndMs = performance.now();
      this._emitProfilerTickRecord({
        tickStartMs, syncEndMs, stepEndMs, captureEndMs, synthEndMs,
        frame, localCh, synth,
        ackTickObserved: newestSnap ? (newestSnap.players.get(this.localCharId)?.lastProcessedInputTick ?? -1) : -1,
        remoteInputs: profRemoteInputs,
        dtMs: dt * 1000
      });
    }

    if (NETCODE_TEL) {
      const telNow = performance.now();
      if (newestSnap) {
        const snapStale = telNow - newestSnap.receivedAtMs;
        this.telSnapStaleSum += snapStale;
        if (snapStale > this.telSnapStaleMax) this.telSnapStaleMax = snapStale;
        const localSlot = newestSnap.players.get(this.localCharId);
        if (localSlot && localSlot.lastProcessedInputTick > 0) {
          const ackLag = this.tickCounter - localSlot.lastProcessedInputTick;
          this.telAckLagSum += ackLag;
          if (ackLag > this.telAckLagMax) this.telAckLagMax = ackLag;
          this.telAckLagSamples += 1;
        }
      }
      if (this.renderTimeInitialized) {
        const renderLag = telNow - this.renderTimeMs;
        this.telRenderLagSum += renderLag;
        if (renderLag > this.telRenderLagMax) this.telRenderLagMax = renderLag;
      }
      this.telTicksThisWindow += 1;
      if (this.telTicksThisWindow >= 60) {
        const windowMs = this.telLastWindowWallMs > 0 ? telNow - this.telLastWindowWallMs : 1000;
        const n = this.telTicksThisWindow;
        const ackN = Math.max(1, this.telAckLagSamples);
        const interN = Math.max(1, this.telInterArrivalSamples);
        console.log(
          `[net-tel char=${this.localCharId}] tick=${this.tickCounter} winMs=${windowMs.toFixed(0)} | ` +
          `ackLag avg=${(this.telAckLagSum / ackN).toFixed(1)}t max=${this.telAckLagMax}t (≈${(this.telAckLagSum / ackN * 16.67).toFixed(0)}ms) | ` +
          `snapStale avg=${(this.telSnapStaleSum / n).toFixed(1)}ms max=${this.telSnapStaleMax.toFixed(0)}ms | ` +
          `renderLag avg=${(this.telRenderLagSum / n).toFixed(1)}ms max=${this.telRenderLagMax.toFixed(0)}ms | ` +
          `interArr avg=${(this.telInterArrivalSum / interN).toFixed(1)}ms max=${this.telInterArrivalMax.toFixed(0)}ms | ` +
          `interpDelay=${this.interpDelayMs.toFixed(1)}ms`
        );
        this.telAckLagSum = 0;
        this.telAckLagMax = 0;
        this.telAckLagSamples = 0;
        this.telSnapStaleSum = 0;
        this.telSnapStaleMax = 0;
        this.telRenderLagSum = 0;
        this.telRenderLagMax = 0;
        this.telInterArrivalSum = 0;
        this.telInterArrivalMax = 0;
        this.telInterArrivalSamples = 0;
        this.telTicksThisWindow = 0;
        this.telLastWindowWallMs = telNow;
      } else if (this.telTicksThisWindow === 1) {
        this.telLastWindowWallMs = telNow;
      }
    }

    this.tickCounter += 1;

    // Spectate focus (unchanged).
    const phase = this.match.phase;
    if (phase === 'playing') {
      if (this.prevMatchPhase !== 'playing') {
        this.match.focusId = this.match.localPlayer().id;
        // Round just (re)started — open the keepalive burst window so the
        // character's freshly-spawned ragdoll gets nudged out of any
        // settle-pose Rapier might park it in during the first second.
        this.roundPlayingSinceMs = performance.now();
      }
      this.updateFocusForSpectate();
    }
    this.prevMatchPhase = phase;
  }

  /** Per-rAF render hook. Advances renderTimeMs by the rAF wall delta and
   *  rebuilds bracket-interpolated bones for every non-local character into
   *  `_remoteBoneScratch`. The renderer reads the scratch via
   *  `remoteBonesAt()` and writes straight onto the meshes — at 120Hz that's
   *  120 distinct remote poses per second, instead of the two-copies-of-each
   *  60Hz synth pose you'd get from leaning on the renderer's prev/curr
   *  alpha-lerp alone.
   *
   *  Call from `onRender(alpha, realDt)` in the GameLoop, BEFORE
   *  pollAndSendLiveInput, so the bones the renderer is about to read are
   *  coherent with this rAF's wall instant. */
  renderTick(realDtMs: number): void {
    const newestSnap = this.snapBuffer[this.snapBuffer.length - 1];
    if (!this.renderTimeInitialized && newestSnap) {
      this.renderTimeMs = newestSnap.receivedAtMs - this.interpDelayMs;
      this.renderTimeInitialized = true;
    } else if (this.renderTimeInitialized) {
      this.renderTimeMs += realDtMs;
      if (newestSnap) {
        const targetTime = newestSnap.receivedAtMs - this.interpDelayMs;
        if (this.renderTimeMs > targetTime) {
          this.renderTimeMs = targetTime;
        } else {
          // Proportional catch-up: close 40% of any behind-target gap per
          // rAF. Rides on top of the wallDelta advance — rendering keeps
          // interpolating continuously. Drains residual drift that builds
          // up during the interpDelay decay transient (1-2s after match
          // start). 40%/rAF reaches 95% of target in ~6 frames.
          const gap = targetTime - this.renderTimeMs;
          if (gap > 0) this.renderTimeMs += gap * 0.4;
          // Hard snap for catastrophic lag (tab just unbackgrounded, etc.).
          if (targetTime - this.renderTimeMs > MAX_INTERP_LAG_MS) {
            this.renderTimeMs = targetTime;
          }
        }
      }
    }

    if (this.bonesBuffer.length === 0) return;
    const { a: bonesA, b: bonesB, t: bonesT } = bracket(this.bonesBuffer, this.renderTimeMs);
    for (const m of this.match.members) {
      if (m.id === this.localCharId) continue;
      let scratch = this._remoteBoneScratch.get(m.id);
      if (!scratch || scratch.length !== this.boneCount * FLOATS_PER_BONE_RENDER) {
        scratch = new Float32Array(this.boneCount * FLOATS_PER_BONE_RENDER);
        this._remoteBoneScratch.set(m.id, scratch);
      }
      const wireA = bonesA?.perChar.get(m.id) ?? null;
      const wireB = bonesB?.perChar.get(m.id) ?? wireA;
      if (wireA && wireB) {
        interpBonesFromWire(scratch, wireA, wireB, bonesT, this.boneCount);
      } else if (wireA) {
        copyPosRotFromWire(scratch, wireA, this.boneCount);
      } else if (wireB) {
        copyPosRotFromWire(scratch, wireB, this.boneCount);
      }
    }
  }

  /** Latest per-rAF bracket-interpolated bones for a remote character, in
   *  render format (7 floats/bone: pos+quat). Returns null for the local
   *  character or before the first renderTick has populated the scratch. */
  remoteBonesAt(charId: number): Float32Array | null {
    if (charId === this.localCharId) return null;
    return this._remoteBoneScratch.get(charId) ?? null;
  }

  /** Poll local input and flush it to the server. Call once per rAF frame
   *  (before `tick()`-driven rendering) so keypress→wire latency is bounded
   *  by monitor refresh rate, not the 60Hz sim fixed step. */
  pollAndSendLiveInput(realDt: number): void {
    const frame = this.input.poll(this.tickCounter, realDt);
    this.maybeInjectKeepalive(frame);
    this.liveInputFrame = frame;
    if (!(this.latest?.roundOver ?? false)) {
      this.recordAndSendInput(frame);
    }
  }

  /** If the human is idle (or the round just (re)started), mutate `frame`
   *  in-place to carry a tiny moveZ + the Keepalive flag. See KEEPALIVE_*
   *  constants for the timing model. Mutates upstream of `recordAndSendInput`
   *  so the rolling history + rollback prediction both see the same modified
   *  frame the server will. */
  private maybeInjectKeepalive(frame: InputFrame): void {
    const now = performance.now();

    // "Real input" = anything the human did this frame. Successive
    // keepalives must NOT keep refreshing this stamp or the idle gate
    // would never trip. The Keepalive flag is masked off explicitly.
    const realButtons = frame.buttons & ~InputBtn.Keepalive;
    const isReal = frame.moveX !== 0 || frame.moveZ !== 0 || realButtons !== 0;
    if (isReal) {
      this.lastRealInputAtMs = now;
      return;
    }

    // Suppress while the round isn't actively playing — inputs are frozen
    // server-side anyway, so keepalives during round-over would just be
    // wasted bytes.
    if (this.latest?.roundOver ?? false) return;

    let shouldFire = false;

    // Round-start burst.
    if (
      this.roundPlayingSinceMs >= 0 &&
      now - this.roundPlayingSinceMs < ROUND_START_KEEPALIVE_WINDOW_MS &&
      (this.lastKeepaliveAtMs < 0 ||
        now - this.lastKeepaliveAtMs >= ROUND_START_KEEPALIVE_INTERVAL_MS)
    ) {
      shouldFire = true;
    }

    // Steady-state idle keepalive.
    if (
      !shouldFire &&
      this.lastRealInputAtMs >= 0 &&
      now - this.lastRealInputAtMs >= KEEPALIVE_IDLE_THRESHOLD_MS &&
      (this.lastKeepaliveAtMs < 0 ||
        now - this.lastKeepaliveAtMs >= KEEPALIVE_INTERVAL_MS)
    ) {
      shouldFire = true;
    }

    if (!shouldFire) return;

    frame.moveZ = KEEPALIVE_MOVE_Z;
    frame.buttons = (frame.buttons | InputBtn.Keepalive) & 0xff;
    this.lastKeepaliveAtMs = now;
  }

  localPlayerIds(): number[] { return [this.localCharId]; }
  latestSnapshot(): SimState | null { return this.latest; }
  prevSnapshot(): SimState | null { return this.prev; }
  snapshotAtTick(t: number): SimState | null {
    if (this.latest == null || t < 0 || t > this.latest.tick) return null;
    if (t < this.oldestBufferedTick()) return null;
    return this.buffer[t % BUFFER_LEN];
  }
  oldestBufferedTick(): number {
    if (this.latest == null) return 0;
    return Math.max(0, this.latest.tick - BUFFER_LEN + 1);
  }
  forceReset(): void { /* server is authoritative; debug reset is a no-op online */ }
  setDifficulty(_d: AIDifficulty): void { /* AI lives on server */ }
  advanceToNextRound(): void { /* server controls round advancement */ }

  // -- Netcode profiler remote-control --------------------------------------
  //
  // Thin wrappers around room.send so HUD / DevTools code doesn't need a
  // direct reference to the Colyseus Room. Server responds via
  // 'profile:dump' / 'profile:error' which we translate back into a
  // Promise here.

  private _pendingDumpResolve: ((msg: ProfileDumpMessage) => void) | null = null;
  private _pendingDumpReject: ((err: Error) => void) | null = null;

  /** Ask the server to begin server-side recording. Also starts the client
   *  recorder. sessionId is the caller's choice — typically a short UUID so
   *  both halves of the dump correlate. */
  profileStart(sessionId: string): void {
    netcodeProfiler.start({ sessionId, localCharId: this.localCharId });
    netcodeProfiler.setRollbackCountStart(this.rollbackCount);
    try { this.room.send('profile:start', { sessionId }); } catch { /* noop */ }
  }

  /** Stop both sides. Does NOT fetch the dump — call `requestProfileDump()`
   *  when you want the server half back. Server will auto-stop on dump
   *  request too, so this is mostly used for "pause then abandon". */
  profileStop(): void {
    netcodeProfiler.setRollbackCountEnd(this.rollbackCount);
    netcodeProfiler.stop();
    try { this.room.send('profile:stop', {}); } catch { /* noop */ }
  }

  /** Request the server dump. Resolves with the server half (plain object);
   *  the client half lives in netcodeProfiler.getProfile(). The exporter
   *  combines both into a single JSON blob for download. */
  requestProfileDump(): Promise<ProfileDumpMessage> {
    netcodeProfiler.setRollbackCountEnd(this.rollbackCount);
    netcodeProfiler.stop();
    return new Promise<ProfileDumpMessage>((resolve, reject) => {
      this._pendingDumpResolve = resolve;
      this._pendingDumpReject = reject;
      try { this.room.send('profile:request-dump', {}); } catch (e) {
        this._pendingDumpResolve = null;
        this._pendingDumpReject = null;
        reject(e as Error);
      }
      // Safety timeout — server could disappear.
      setTimeout(() => {
        if (this._pendingDumpResolve === resolve) {
          this._pendingDumpResolve = null;
          this._pendingDumpReject = null;
          reject(new Error('profile dump request timed out after 10s'));
        }
      }, 10_000);
    });
  }

  cycleFocus(dir: -1 | 1): void {
    const snap = this.latest;
    if (!snap) return;
    const local = this.match.localPlayer();
    const localDead = isDead(snap, local.id);
    const candidates: number[] = [];
    for (const m of this.match.members) {
      if (m.isLocal && localDead) continue;
      if (isDead(snap, m.id)) continue;
      candidates.push(m.id);
    }
    if (candidates.length === 0) return;
    const currIdx = candidates.indexOf(this.match.focusId);
    const n = candidates.length;
    const next = currIdx < 0
      ? candidates[0]!
      : candidates[(currIdx + dir + n) % n]!;
    this.match.focusId = next;
  }
  setFocusId(id: number): void {
    if (this.match.memberById(id)) this.match.focusId = id;
  }

  private updateFocusForSpectate(): void {
    const snap = this.latest;
    if (!snap) return;
    const local = this.match.localPlayer();
    const localDead = isDead(snap, local.id);
    if (!localDead) {
      this.match.focusId = local.id;
      return;
    }
    if (this.match.focusId !== local.id && !isDead(snap, this.match.focusId)) return;
    const teammate = this.match.members.find((m) => !m.isLocal && m.team === local.team);
    if (teammate && !isDead(snap, teammate.id)) {
      this.match.focusId = teammate.id;
      return;
    }
    const anyAlive = this.match.members.find((m) => !isDead(snap, m.id));
    if (anyAlive) this.match.focusId = anyAlive.id;
  }

  pingFor(charId: number): number {
    return this.snapBuffer[this.snapBuffer.length - 1]?.players.get(charId)?.ping ?? 0;
  }

  dispose(): void {
    try { this.room.removeAllListeners(); } catch { /* noop */ }
  }

  // -- Server message handlers ----------------------------------------------

  private onSchemaPatch(state: GameStateLike): void {
    if (state.tick === undefined) return;
    const prof = netcodeProfiler.isRecording();
    const reconcileStartMs = prof ? performance.now() : 0;
    const players = new Map<number, PlayerSlotState>();
    for (const p of state.players) players.set(p.charId, cloneSlot(p));
    const receivedAtMs = performance.now();
    const snap: ServerSnapshot = {
      tick: state.tick,
      receivedAtMs,
      players,
      phase: state.phase || 'playing',
      round: state.round,
      crowns: { red: state.crownsRed, blue: state.crownsBlue },
      lastRoundWinner:
        state.lastRoundWinner === 'red' ? 'red'
        : state.lastRoundWinner === 'blue' ? 'blue'
        : null,
      shipRoll: state.shipRoll ?? 0,
      shipYaw: state.shipYaw ?? 0,
      shipSinkY: state.shipSinkY ?? 0
    };
    this.snapBuffer.push(snap);
    if (this.snapBuffer.length > SNAP_BUFFER_LEN) this.snapBuffer.shift();

    if (this.lastPatchReceivedAtMs >= 0) {
      const interarrival = receivedAtMs - this.lastPatchReceivedAtMs;
      this.interarrivalSamples.push(interarrival);
      if (this.interarrivalSamples.length > INTERP_JITTER_WINDOW) {
        this.interarrivalSamples.shift();
      }
      this.updateInterpDelay();
      if (NETCODE_TEL) {
        this.telInterArrivalSum += interarrival;
        if (interarrival > this.telInterArrivalMax) this.telInterArrivalMax = interarrival;
        this.telInterArrivalSamples += 1;
      }
    }
    this.lastPatchReceivedAtMs = receivedAtMs;

    const prevPhase = this.match.phase;
    const prevCrowns = { red: this.match.crowns.red, blue: this.match.crowns.blue };
    this.match.phase = snap.phase as MatchState['phase'];
    this.match.round = snap.round;
    this.match.crowns.red = snap.crowns.red;
    this.match.crowns.blue = snap.crowns.blue;
    this.match.lastRoundWinner = snap.lastRoundWinner;
    // Mirror MatchState.applyRoundWin() — the round-end modal reads
    // crownsBeforeLatestWin to know which crown slot just popped on. The
    // local-sim path sets it via applyRoundWin; online we bypass that and
    // overwrite crowns directly from snapshots, so capture the pre-update
    // values on the playing→roundOver/gameOver transition.
    if (prevPhase === 'playing' && this.match.phase !== 'playing') {
      this.match.crownsBeforeLatestWin = prevCrowns;
    }

    // Round boundary: if we just transitioned from a non-playing phase back
    // to 'playing', the server has reset every character to spawn. Force
    // our World through the same reset so the client sim doesn't carry
    // over ragdoll velocity from the previous round's final moments.
    if (prevPhase !== 'playing' && this.match.phase === 'playing') {
      this.world.forceReset();
      // Prediction buffers are now referencing a stale pose; invalidate so
      // the next rollback compare doesn't diff against pre-reset data.
      this.predictedTickBuffer.fill(-1);
    }

    // Compute client-side grab coupling once per patch so the local
    // reconcile path can avoid snapping through active CLIENT joints. In the
    // stabilized online model remotes do not live in the local solver, so
    // server-side grab flags alone must not suppress reconciliation.
    const coupled = this.buildGrabCoupledSet(snap);

    // Reconcile local prediction against server authority.
    const localSlot = snap.players.get(this.localCharId);
    if (localSlot) this.reconcileFromServer(localSlot, coupled);

    if (prof) {
      // Estimate patch bytes: Colyseus delta-encodes, but as a rough upper
      // bound we count the slot fields we care about. Replace with a real
      // figure if Colyseus ever exposes it.
      const estimatedBytes = 32 + state.players.length * 110;
      netcodeProfiler.recordPatch({
        serverTick: state.tick,
        estimatedBytes,
        playerCount: state.players.length
      });
      void reconcileStartMs;
    }
  }

  /** Returns the set of charIds currently involved in CLIENT-side grab
   *  joints. In the stabilized online model this is local-only. */
  private buildGrabCoupledSet(snap: ServerSnapshot | null): Set<number> {
    void snap;
    const out = this._grabCoupledScratch;
    out.clear();
    for (const grabber of this.world.characters) {
      if (!grabber.grab.isGrabbing()) continue;
      out.add(grabber.id);
      for (const victim of this.world.characters) {
        if (victim === grabber) continue;
        if (grabber.grab.countOn(victim) > 0) out.add(victim.id);
      }
    }
    return out;
  }

  /** Phase 2.6. Release every Rapier spherical joint that either involves
   *  `target` as a grabber OR has `target` as a victim, from any
   *  attacker. Called immediately before a catastrophic-drift snap so the
   *  snap lands on a joint-free ragdoll. If the grab button remains held,
   *  `Grab.update()` re-latches next tick at the post-snap pose. */
  private releaseAllGrabsInvolving(target: Character): void {
    for (const grabber of this.world.characters) {
      if (grabber === target) {
        grabber.grab.releaseAll();
      } else {
        grabber.grab.releaseAllOn(target);
      }
    }
  }

  private onBonesMessage(bytes: Uint8Array): void {
    // Layout (matches GameRoom.broadcastBones):
    //   per char: [charId(uint8), boneCount(uint8), pad(uint16), 13×bones float32s]
    const perChar = new Map<number, Float32Array>();
    let boneCount = this.boneCount;
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    let off = 0;
    while (off < bytes.byteLength) {
      if (off + 4 > bytes.byteLength) {
        // Truncated header — abandon this message rather than overrunning.
        // Almost always indicates a client/server wire-format mismatch.
        console.warn('[ColyseusTransport] bones message truncated at header; ignoring');
        return;
      }
      const charId = view.getUint8(off);
      boneCount = view.getUint8(off + 1);
      off += 4;
      const n = boneCount * FLOATS_PER_BONE_WIRE;
      const floatBytes = n * 4;
      if (off + floatBytes > bytes.byteLength) {
        // Same guard — if the server is still emitting the pre-Phase-1
        // 7-floats-per-bone layout, we'll end up here on the 2nd char and
        // this catch prevents a stream of RangeError exceptions that would
        // otherwise bury the console.
        console.warn(
          `[ColyseusTransport] bones message wire-format mismatch ` +
          `(need ${floatBytes} bytes at offset ${off}, have ${bytes.byteLength}). ` +
          `Restart the server to pick up the Phase-1 13-floats-per-bone layout.`
        );
        return;
      }
      const floats = new Float32Array(n);
      for (let i = 0; i < n; i++) {
        floats[i] = view.getFloat32(off + i * 4, true /* little-endian */);
      }
      perChar.set(charId, floats);
      off += floatBytes;
    }
    if (boneCount !== this.knownBoneCount) this.knownBoneCount = boneCount;
    this.boneCount = boneCount;
    const newest = this.snapBuffer[this.snapBuffer.length - 1];
    this.bonesBuffer.push({
      tick: newest?.tick ?? 0,
      receivedAtMs: performance.now(),
      perChar
    });
    if (this.bonesBuffer.length > SNAP_BUFFER_LEN) this.bonesBuffer.shift();
    if (netcodeProfiler.isRecording()) {
      netcodeProfiler.recordBonesMessage({
        serverTickApprox: newest?.tick ?? -1,
        sizeBytes: bytes.byteLength,
        charsIncluded: perChar.size
      });
    }
  }

  /** Append `frame` to the sent-history ring (deduping by tick — the rAF
   *  cadence often runs faster than the sim tick cadence, so multiple polls
   *  share a tick and the newer one should replace the older in the
   *  bundle). Then ship the full history to the server — the server's
   *  tick-keyed input queue rejects stale frames and dedups by tick, so
   *  redundant older frames just act as insurance against packet loss. */
  private recordAndSendInput(frame: InputFrame): void {
    const hist = this.sentInputHistory;
    const tail = hist[hist.length - 1];
    if (tail && tail.tick === frame.tick) {
      // Same-tick re-poll — overwrite in place so only the newest sample
      // for this tick goes out (most recent button/stick state).
      hist[hist.length - 1] = frame;
    } else {
      hist.push(frame);
      if (hist.length > SENT_INPUT_HISTORY_LEN) hist.shift();
    }
    try {
      this.room.send('input', { frames: hist });
    } catch { /* room may be closing */ }
    if (netcodeProfiler.isRecording()) {
      netcodeProfiler.recordInputSend({
        clientTick: frame.tick,
        bundleSize: hist.length,
        // 5 scalars × 4 bytes per frame + overhead; ballpark.
        estimatedBytes: hist.length * 28
      });
    }
  }

  // -- Rollback reconciliation ---------------------------------------------

  /** Called once per schema patch with the local slot's server-authoritative
   *  fields. Compares predictedBonesBuffer[ackTick] against the server's
   *  current pose; on divergence snaps the Ragdoll to the server's state
   *  at ackTick then REPLAYS un-acked inputs to catch back up to the
   *  present (Phase 1.5). */
  private reconcileFromServer(slot: PlayerSlotState, coupled: Set<number>): void {
    const localCh = this.localCharacter;
    if (!localCh) return;
    const prof = netcodeProfiler.isRecording();
    const newestBones = this.bonesBuffer[this.bonesBuffer.length - 1];
    const serverLocalBones = newestBones?.perChar.get(this.localCharId);

    // Initial seed: before we've stepped anywhere meaningful, or the server
    // hasn't yet processed any input for us (ackTick == 0 on a fresh join),
    // just snap to the server's current bones if we have them.
    if (!this.reconcileInitialized) {
      if (serverLocalBones) {
        this.snapLocalToServer(serverLocalBones);
        this.reconcileInitialized = true;
        if (prof) this._emitReconcileEvent('seed', slot, -1, -1, 0, []);
      }
      return;
    }

    // Round-end / game-end: input is gated off (see tick():620) and the local
    // body is a ragdoll being shoved by the other 3 ragdolls on the server —
    // bodies the client's local sim doesn't contain (remotes are render-only
    // here). Predicted pose diverges every tick, ackTick freezes, and
    // rollbackAndReplay's per-call replay length grows unboundedly. On
    // Saturn Rings each replayed tick re-integrates 480 rocks, producing a
    // runaway feedback storm (~1100 rock-sim ticks/sec, FPS → 8). There is
    // nothing meaningful to predict in this window, so snap to the server's
    // current bones each patch and skip the replay entirely.
    if (this.match.phase !== 'playing') {
      if (serverLocalBones) this.snapLocalToServer(serverLocalBones);
      return;
    }

    const ackTick = slot.lastProcessedInputTick;
    if (ackTick <= 0) {
      // Server has never acked one of our inputs. Happens for any player
      // who hasn't pressed a movement/button since joining — the server's
      // `dequeueInputsForTick` has nothing in their queue and no
      // `lastConsumedInput` to extrapolate, so it never sets anything into
      // `humanInputs`, so `updateAckTicks` never writes a non-zero
      // `lastProcessedInputTick` for this slot.
      //
      // We have no anchor tick for rollback-replay, but we MUST still
      // follow server authority for pose: otherwise being GRABBED by
      // another player before we've pressed any key leaves our local body
      // stranded at spawn while the server is dragging us around. That's
      // the "body anchored / mid-air dragged" visual on the victim's own
      // client — pressing any key "fixes" it only because that's when
      // ackTick finally becomes non-zero and the full reconcile path
      // below takes over.
      //
      // Drift-based hard snap; no replay (nothing to replay against).
      if (!serverLocalBones) return;
      const pelvis = localCh.ragdoll.pelvis().translation();
      const dx = slot.pelvisX - pelvis.x;
      const dy = slot.pelvisY - pelvis.y;
      const dz = slot.pelvisZ - pelvis.z;
      const drift = Math.hypot(dx, dy, dz);
      if (drift < RECONCILE_POSITION_THRESHOLD) return;
      this.snapLocalToServer(serverLocalBones);
      this.rollbackCount += 1;
      this.lastReconcileDrift = drift;
      if (prof) this._emitReconcileEvent('snap-no-ack', slot, ackTick, -1, drift, []);
      return;
    }

    // `tickCounter` points at the NEXT tick to run; the newest tick we've
    // already buffered is `tickCounter - 1`. That's the frame the replay
    // must fast-forward us back to.
    const lastCompletedTick = this.tickCounter - 1;
    const ageTicks = lastCompletedTick - ackTick;
    if (ageTicks < 0 || ageTicks >= HARD_SNAP_AGE_TICKS) {
      if (serverLocalBones) this.snapLocalToServer(serverLocalBones);
      if (prof) this._emitReconcileEvent('snap-stale', slot, ackTick, ageTicks, 0, []);
      return;
    }

    const predicted = this.predictedBonesForTick(ackTick);
    if (!predicted) {
      // Tick outside buffer — snap, no replay possible.
      if (serverLocalBones) this.snapLocalToServer(serverLocalBones);
      if (prof) this._emitReconcileEvent('skip-no-predicted', slot, ackTick, ageTicks, 0, []);
      return;
    }

    // Pelvis is bone index 0 → floats 0..2 of the 13-float stride.
    const dx = slot.pelvisX - predicted[0]!;
    const dy = slot.pelvisY - predicted[1]!;
    const dz = slot.pelvisZ - predicted[2]!;
    const drift = Math.hypot(dx, dy, dz);
    this.lastReconcileDrift = drift;

    // Compute per-bone drift vs server bones if we have them, for the
    // profiler record. This is cheap (9 hypots) and diagnostic gold.
    let perBoneDrift: number[] = [];
    if (prof && serverLocalBones) {
      perBoneDrift = new Array(this.boneCount);
      for (let i = 0; i < this.boneCount; i++) {
        const po = i * FLOATS_PER_BONE_WIRE;
        const sx = serverLocalBones[po] ?? 0;
        const sy = serverLocalBones[po + 1] ?? 0;
        const sz = serverLocalBones[po + 2] ?? 0;
        const px = predicted[po] ?? 0;
        const py = predicted[po + 1] ?? 0;
        const pz = predicted[po + 2] ?? 0;
        perBoneDrift[i] = Math.hypot(sx - px, sy - py, sz - pz);
      }
    }

    if (drift < RECONCILE_POSITION_THRESHOLD) {
      // Prediction matched — hot path, nothing to do.
      if (prof) this._emitReconcileEvent('noop', slot, ackTick, ageTicks, drift, perBoneDrift);
      return;
    }

    if (!serverLocalBones) {
      // Bones message hasn't landed yet for this patch — can't snap
      // physics without velocities. Wait for the next bones msg.
      if (prof) this._emitReconcileEvent('skip-no-bones', slot, ackTick, ageTicks, drift, perBoneDrift);
      return;
    }

    // Phase 2.6: if we're currently involved in a grab (as grabber or
    // victim), a rollback+replay would `loadBonesFull` the local
    // ragdoll — but the spherical joints anchoring hand↔victim would
    // then see a large constraint violation and limbs explode. Tolerate
    // sub-catastrophic drift until the grab ends. Above the catastrophic
    // threshold we release every joint touching us first so the snap
    // lands clean.
    if (coupled.has(this.localCharId)) {
      if (drift < GRAB_COUPLED_HARD_SNAP_THRESHOLD) {
        if (prof) this._emitReconcileEvent('skip-grab-coupled', slot, ackTick, ageTicks, drift, perBoneDrift);
        return;
      }
      this.releaseAllGrabsInvolving(localCh);
    }

    // Divergence above threshold → rollback-and-replay.
    let prePelvisX = 0, prePelvisY = 0, prePelvisZ = 0;
    let preBonesForProf: Float32Array | null = null;
    if (prof) {
      const pelvis = localCh.ragdoll.pelvis().translation();
      prePelvisX = pelvis.x; prePelvisY = pelvis.y; prePelvisZ = pelvis.z;
      if (!this._profPreRollbackBones || this._profPreRollbackBones.length !== this.boneCount * 7) {
        this._profPreRollbackBones = new Float32Array(this.boneCount * 7);
      }
      localCh.ragdoll.writeBones(this._profPreRollbackBones);
      preBonesForProf = this._profPreRollbackBones;
    }
    this.rollbackAndReplay(localCh, serverLocalBones, ackTick, lastCompletedTick, slot);
    this.rollbackCount += 1;
    if (prof) {
      const pelvisPost = localCh.ragdoll.pelvis().translation();
      if (!this._profPostRollbackBones || this._profPostRollbackBones.length !== this.boneCount * 7) {
        this._profPostRollbackBones = new Float32Array(this.boneCount * 7);
      }
      localCh.ragdoll.writeBones(this._profPostRollbackBones);
      const postBones = this._profPostRollbackBones;
      const postReplayPerBoneDelta = new Array<number>(this.boneCount);
      for (let i = 0; i < this.boneCount; i++) {
        const o = i * 7;
        const dxi = postBones[o]! - (preBonesForProf?.[o] ?? 0);
        const dyi = postBones[o + 1]! - (preBonesForProf?.[o + 1] ?? 0);
        const dzi = postBones[o + 2]! - (preBonesForProf?.[o + 2] ?? 0);
        postReplayPerBoneDelta[i] = Math.hypot(dxi, dyi, dzi);
      }
      const replayedTicks = Math.max(0, lastCompletedTick - ackTick);
      const evt: ClientReconcileEvent = {
        wallTimeMs: 0,
        clientTick: lastCompletedTick,
        serverAckTick: ackTick,
        ageTicks,
        pelvisDriftM: drift,
        perBoneDriftM: perBoneDrift,
        action: 'rollback',
        replayedTicks,
        prePelvisX, prePelvisY, prePelvisZ,
        postPelvisX: pelvisPost.x, postPelvisY: pelvisPost.y, postPelvisZ: pelvisPost.z,
        postReplayPerBoneDeltaM: postReplayPerBoneDelta
      };
      netcodeProfiler.recordReconcile(evt);
    }
  }

  private _profPreRollbackBones: Float32Array | null = null;
  private _profPostRollbackBones: Float32Array | null = null;

  private _emitReconcileEvent(
    action: ClientReconcileEvent['action'],
    slot: PlayerSlotState,
    ackTick: number,
    ageTicks: number,
    drift: number,
    perBoneDrift: number[]
  ): void {
    void slot;
    const evt: ClientReconcileEvent = {
      wallTimeMs: 0,
      clientTick: this.tickCounter - 1,
      serverAckTick: ackTick,
      ageTicks,
      pelvisDriftM: drift,
      perBoneDriftM: perBoneDrift,
      action,
      replayedTicks: 0
    };
    netcodeProfiler.recordReconcile(evt);
  }

  /** The Phase 1.5 rollback:
   *   1. Snap Ragdoll bodies (pos + rot + linvel + angvel) to the server's
   *      current pose (server has told us this reflects the effect of our
   *      input at ackTick).
   *   2. Restore Character scalar state to what we had at ackTick, then
   *      overlay server-authoritative scalars (HP/KO/stun/wasted/etc.)
   *      from the ack slot in case those also drifted.
   *   3. Replay each un-acked InputFrame (ackTick+1 → lastCompletedTick)
   *      by stepping the world. Re-populate predictedBones /
   *      predictedCharState for each replayed tick so the next server ack
   *      has something accurate to compare against. */
  private rollbackAndReplay(
    localCh: Character,
    serverLocalBones: Float32Array,
    ackTick: number,
    lastCompletedTick: number,
    ackSlot: PlayerSlotState
  ): void {
    // Step 1 — physics snap.
    localCh.ragdoll.loadBonesFull(serverLocalBones);

    // Step 2 — restore Character internal state. The ring slot for ackTick
    // was written AFTER Character.step consumed input[ackTick], so it is
    // exactly the state the server saw when producing this snapshot
    // (modulo server-auth scalars, which we overlay next).
    const charAtAck = this.charStateForTick(this.localCharId, ackTick);
    if (charAtAck) {
      localCh.loadInternalState(charAtAck);
    }
    // Overlay server authority on scalars. If our locally-saved state was
    // stale (e.g. we hadn't yet received the hit that the server already
    // applied at ackTick), this brings hp/ko/stun back to the truth before
    // we replay.
    this.syncLocalScalar(localCh, ackSlot);

    // Step 3 — replay buffered inputs ackTick+1 → lastCompletedTick. Local
    // char uses its saved input; remotes use their last-consumed forwarded
    // input (we don't index remote inputs by client tick, so we can't
    // perfectly replay each remote's exact per-tick input — but any drift
    // is bounded by the next server patch snap).
    const fixedDt = 1 / TICK_HZ;
    const inputs = this._stepInputsScratch;
    for (let t = ackTick + 1; t <= lastCompletedTick; t++) {
      const tIdx = t % PRED_BUFFER_LEN;
      inputs.clear();
      const localFrame = (this.predictedTickBuffer[tIdx] === t)
        ? this.inputBuffer[tIdx]
        : null;
      if (localFrame) {
        inputs.set(this.localCharId, localFrame);
      } else {
        inputs.set(this.localCharId, { tick: t, moveX: 0, moveZ: 0, aimYaw: 0, buttons: 0 });
      }
      for (const m of this.match.members) {
        if (m.id === this.localCharId) continue;
        const remoteFrame = this.remoteLastConsumed.get(m.id);
        if (remoteFrame) inputs.set(m.id, remoteFrame);
      }
      this.world.step(fixedDt, inputs);
      // Re-capture every char's state at this replayed tick so a future
      // server ack compares against our POST-ROLLBACK belief.
      this.capturePredictedStateAll(t);
    }
  }

  /** Save every character's post-step bone + scalar state into the prediction
   *  ring at `tick`. Ring slots are pre-allocated in the constructor for
   *  each roster member; the Float32Array entries are lazily sized on first
   *  use so we don't need to know `boneCount` at construction time. */
  private capturePredictedStateAll(tick: number): void {
    const idx = tick % PRED_BUFFER_LEN;
    const needed = this.boneCount * FLOATS_PER_BONE_WIRE;
    for (const m of this.match.members) {
      const ch = this.charactersById.get(m.id);
      if (!ch) continue;
      const bonesRing = this.predictedBonesRings.get(m.id);
      if (!bonesRing) continue;
      let bonesSlot = bonesRing[idx];
      if (!bonesSlot || bonesSlot.length !== needed) {
        bonesSlot = new Float32Array(needed);
        bonesRing[idx] = bonesSlot;
      }
      ch.ragdoll.writeBonesFull(bonesSlot);

      const stateRing = this.predictedStateRings.get(m.id);
      if (!stateRing) continue;
      let stateSlot = stateRing[idx];
      if (!stateSlot) {
        stateSlot = makeEmptyInternalState();
        stateRing[idx] = stateSlot;
      }
      ch.saveInternalState(stateSlot);
    }
    this.predictedTickBuffer[idx] = tick;
  }

  /** Pull the oldest forwarded input for a remote char. Same
   *  "oldest-in-queue wins" pattern as the server's Phase 2 dequeue — head
   *  advances past the consumed tick, extrapolates last-consumed on empty
   *  queue without advancing head so a slow forward cadence doesn't
   *  starve the remote character permanently. */
  private dequeueRemoteInput(charId: number): InputFrame | null {
    const queue = this.remoteInputQueues.get(charId);
    let chosenTick = -1;
    let chosen: InputFrame | undefined;
    if (queue && queue.size > 0) {
      for (const [t, f] of queue) {
        if (chosenTick < 0 || t < chosenTick) {
          chosenTick = t;
          chosen = f;
        }
      }
    }
    if (chosen !== undefined) {
      this.remoteLastConsumed.set(charId, chosen);
      queue!.delete(chosenTick);
      this.remoteInputHead.set(charId, chosenTick + 1);
      return chosen;
    }
    // Extrapolate: reuse last consumed if any, head unchanged.
    return this.remoteLastConsumed.get(charId) ?? null;
  }

  private snapLocalToServer(serverLocalBonesFull: Float32Array): void {
    const localCh = this.localCharacter;
    if (!localCh) return;
    if (serverLocalBonesFull.length !== this.boneCount * FLOATS_PER_BONE_WIRE) return;
    localCh.ragdoll.loadBonesFull(serverLocalBonesFull);
  }

  /** Phase 2.5 remote reconciliation: snap every non-local character's
   *  Ragdoll bodies + scalar state to the server's authoritative values on
   *  each patch. Between patches the client sim advances these characters
   *  locally from forwarded inputs so the visible motion isn't bottlenecked
   *  by the server's bones broadcast cadence; the per-patch snap bounds
   *  any drift from Rapier float non-determinism to a single patch
   *  interval. */
  private snapRemotesToServer(snap: ServerSnapshot, coupled: Set<number>): void {
    const newestBones = this.bonesBuffer[this.bonesBuffer.length - 1];
    for (const m of this.match.members) {
      if (m.id === this.localCharId) continue;
      const ch = this.charactersById.get(m.id);
      if (!ch) continue;
      const slot = snap.players.get(m.id);
      if (slot) this.syncLocalScalar(ch, slot);
      const bones = newestBones?.perChar.get(m.id);
      if (!bones || bones.length !== this.boneCount * FLOATS_PER_BONE_WIRE) continue;

      // Phase 2.6: grab-coupled bodies skip the per-body teleport because
      // the active spherical joints interpret the snap as a constraint
      // violation and launch the limbs. Tolerate the drift UNLESS the
      // pelvis has diverged catastrophically (>1.5 m), in which case we
      // release every joint touching this char first and then snap so
      // the pair never stays permanently desynced.
      if (coupled.has(m.id) && slot) {
        const pelvis = ch.ragdoll.pelvis().translation();
        const dx = pelvis.x - slot.pelvisX;
        const dy = pelvis.y - slot.pelvisY;
        const dz = pelvis.z - slot.pelvisZ;
        const drift2 = dx * dx + dy * dy + dz * dz;
        if (drift2 < GRAB_COUPLED_HARD_SNAP_THRESHOLD_SQ) continue;
        this.releaseAllGrabsInvolving(ch);
      }
      ch.ragdoll.loadBonesFull(bones);
    }
  }

  private predictedBonesForTick(clientTick: number): Float32Array | null {
    const idx = clientTick % PRED_BUFFER_LEN;
    if (this.predictedTickBuffer[idx] !== clientTick) return null;
    const ring = this.predictedBonesRings.get(this.localCharId);
    return ring?.[idx] ?? null;
  }

  private charStateForTick(charId: number, clientTick: number): CharacterInternalState | null {
    const idx = clientTick % PRED_BUFFER_LEN;
    if (this.predictedTickBuffer[idx] !== clientTick) return null;
    const ring = this.predictedStateRings.get(charId);
    return ring?.[idx] ?? null;
  }

  /** Mirror server-authoritative scalar fields onto the local Character so
   *  our client-side `Character.step` sees the same HP/KO/stun/stamina as
   *  the server. Phase 1 doesn't predict any of these — they change only in
   *  response to server events (impacts, round resets, etc.). */
  private syncLocalScalar(ch: Character, slot: PlayerSlotState): void {
    ch.hp = slot.hp;
    ch.ko = slot.ko;
    ch.koStunRemaining = slot.koStunRemaining;
    ch.wasted = slot.wasted;
    ch.ringedOut = slot.ringedOut;
    ch.knockedOut = slot.knockedOut;
    ch.stamina = slot.stamina;
    ch.stunRemaining = slot.stunRemaining;
    // staminaLocked is private on Character — skip for now. Phase 1 accepts
    // a brief client-side lag on the "stamina locked" gating transition;
    // the field is still rendered correctly in the HUD because we overwrite
    // cs.staminaLocked from the server slot in synthesizeAt.
  }

  private updateInterpDelay(): void {
    const samples = this.interarrivalSamples;
    const n = samples.length;
    if (n < 2) return;
    let sum = 0;
    for (let i = 0; i < n; i++) sum += samples[i]!;
    const mean = sum / n;
    let sqSum = 0;
    for (let i = 0; i < n; i++) {
      const d = samples[i]! - mean;
      sqSum += d * d;
    }
    const stddev = Math.sqrt(sqSum / n);
    let target = stddev * INTERP_JITTER_MULTIPLIER;
    if (target < INTERP_DELAY_MIN_MS) target = INTERP_DELAY_MIN_MS;
    else if (target > INTERP_DELAY_MAX_MS) target = INTERP_DELAY_MAX_MS;
    const alpha = target > this.interpDelayMs ? INTERP_ATTACK : INTERP_RELEASE;
    this.interpDelayMs += (target - this.interpDelayMs) * alpha;
  }

  // -- Interpolating SimState synth ----------------------------------------

  private synthesizeAt(timeMs: number): SimState {
    const teamById: Record<number, TeamId> = this.match.teamByIdMap();
    if (this.snapBuffer.length === 0) {
      return {
        tick: this.tickCounter,
        characters: this.match.members.map((m) => emptySnapshot(m.id, this.boneCount)),
        impacts: [],
        footsteps: [],
        swings: [],
        splashes: [],
        roundOver: false,
        winningTeam: null,
        teamById
      };
    }

    // Local character renders from the client's predicted Rapier world.
    // Remotes render from the server's snapshot + bones buffers so they
    // never feed back into local prediction through shared physics.
    const { a: snapA, b: snapB, t: snapT } = bracket(this.snapBuffer, timeMs);
    const { a: bonesA, b: bonesB, t: bonesT } = bracket(this.bonesBuffer, timeMs);
    const newestSnap = this.snapBuffer[this.snapBuffer.length - 1];

    const characters: CharacterSnapshot[] = [];
    for (const m of this.match.members) {
      const cs = emptySnapshot(m.id, this.boneCount);
      const ch = this.charactersById.get(m.id);
      const slotA = snapA?.players.get(m.id) ?? null;
      const slotB = snapB?.players.get(m.id) ?? slotA;
      const slot = slotB ?? slotA ?? newestSnap?.players.get(m.id) ?? null;

      if (ch) {
        ch.writeSnapshot(cs);
        if (slot) {
          if (m.id === this.localCharId) {
            // Local: keep locally-predicted pose, only overlay server-side
            // game-state scalars (HP/KO/stun/etc.). Otherwise the camera
            // and nameplate would track ~ping/2 behind real movement.
            copyServerScalarsInto(cs, slot);
          } else {
            // Remote: server is the source of truth for pose too — overlay
            // the full slot, including pelvis/facing/swing.
            copySlotInto(cs, slot);
          }
        }
      } else {
        const wireA = bonesA?.perChar.get(m.id) ?? null;
        const wireB = bonesB?.perChar.get(m.id) ?? wireA;
        if (wireA && wireB) {
          interpBonesFromWire(cs.bones, wireA, wireB, bonesT, this.boneCount);
        } else if (wireA) {
          copyPosRotFromWire(cs.bones, wireA, this.boneCount);
        } else if (wireB) {
          copyPosRotFromWire(cs.bones, wireB, this.boneCount);
        }
        if (slotA && slotB) {
          copySlotInto(cs, slotB);
          cs.pelvisX = lerp(slotA.pelvisX, slotB.pelvisX, snapT);
          cs.pelvisY = lerp(slotA.pelvisY, slotB.pelvisY, snapT);
          cs.pelvisZ = lerp(slotA.pelvisZ, slotB.pelvisZ, snapT);
          cs.facingYaw = lerpAngle(slotA.facingYaw, slotB.facingYaw, snapT);
          cs.swingPhaseL = lerp(slotA.swingPhaseL, slotB.swingPhaseL, snapT);
          cs.swingPhaseR = lerp(slotA.swingPhaseR, slotB.swingPhaseR, snapT);
        } else if (slot) {
          copySlotInto(cs, slot);
        }
      }
      characters.push(cs);
    }

    // Drain pending events — they happened in real time and shouldn't be
    // delayed by the interpolation buffer.
    const impacts = this.pendingImpacts;
    const footsteps = this.pendingFootsteps;
    const swings = this.pendingSwings;
    const splashes = this.pendingSplashes;
    this.pendingImpacts = [];
    this.pendingFootsteps = [];
    this.pendingSwings = [];
    this.pendingSplashes = [];

    const active = snapB ?? snapA!;
    return {
      tick: active.tick,
      characters,
      impacts,
      footsteps,
      swings,
      splashes,
      roundOver: active.phase !== 'playing',
      winningTeam: active.lastRoundWinner,
      teamById
    };
  }

  // -- Netcode profiler plumbing -------------------------------------------
  //
  // Allocated lazily on first profiler record to stay zero-alloc when no
  // recording is active.
  private _profBonesScratch: Float32Array | null = null;
  private _profInternalStateScratch: CharacterInternalState | null = null;

  private _emitProfilerTickRecord(args: {
    tickStartMs: number;
    syncEndMs: number;
    stepEndMs: number;
    captureEndMs: number;
    synthEndMs: number;
    frame: InputFrame;
    localCh: Character;
    synth: SimState;
    ackTickObserved: number;
    remoteInputs: Array<{ charId: number; depthBefore: number; source: -1 | 0 | 1; consumedTick: number; frame: InputFrame | null }> | null;
    dtMs: number;
  }): void {
    const { tickStartMs, syncEndMs, stepEndMs, captureEndMs, synthEndMs, frame, localCh, synth } = args;
    const localSnap = synth.characters.find((c) => c.id === this.localCharId);
    if (!localSnap) return;

    // Local ragdoll linvel/angvel pelvis — read fresh from physics body.
    const pelvisBody = localCh.ragdoll.pelvis();
    const lv = pelvisBody.linvel();
    const av = pelvisBody.angvel();

    netcodeProfiler.recordSimTick({
      clientTick: this.tickCounter,
      dtMs: args.dtMs,
      tickDurationMs: synthEndMs - tickStartMs,
      stepDurationMs: stepEndMs - syncEndMs,
      synthDurationMs: synthEndMs - captureEndMs,
      syncLocalScalarDurationMs: syncEndMs - tickStartMs,
      capturePredictedDurationMs: captureEndMs - stepEndMs,
      reconcileDurationMs: 0, // filled in by reconcile path; left 0 for the tick-only record
      ackTickObserved: args.ackTickObserved,
      localPelvisX: localSnap.pelvisX,
      localPelvisY: localSnap.pelvisY,
      localPelvisZ: localSnap.pelvisZ,
      localFacingYaw: localSnap.facingYaw,
      localLinvelX: lv.x, localLinvelY: lv.y, localLinvelZ: lv.z,
      localAngvelY: av.y,
      localHp: localSnap.hp,
      localKo: localSnap.ko,
      localStamina: localSnap.stamina,
      localStunRemaining: localSnap.stunRemaining,
      localGrabbing: localSnap.grabbing,
      localBeingGrabbed: localSnap.beingGrabbed,
      inputMoveX: frame.moveX,
      inputMoveZ: frame.moveZ,
      inputAimYaw: frame.aimYaw,
      inputButtons: frame.buttons
    });

    // PD / internal state for the local character.
    if (!this._profInternalStateScratch) {
      this._profInternalStateScratch = makeEmptyInternalState();
    }
    localCh.saveInternalState(this._profInternalStateScratch);
    const s = this._profInternalStateScratch;
    netcodeProfiler.recordPdStateLocal({
      clientTick: this.tickCounter,
      targetYaw: s.targetYaw,
      hipPlantX: s.hipPlantX,
      hipPlantZ: s.hipPlantZ,
      gaitPhase: s.gaitPhase,
      gaitActivity: s.gaitActivity,
      jumpKickActive: s.jumpKickActive,
      punchCooldownL: s.punchCooldownL,
      punchCooldownR: s.punchCooldownR,
      swingPhaseL: (localSnap.swingPhaseL ?? 0),
      swingPhaseR: (localSnap.swingPhaseR ?? 0)
    });

    // Local bone pose (render format).
    if (!this._profBonesScratch) {
      this._profBonesScratch = new Float32Array(this.boneCount * 7);
    }
    localCh.ragdoll.writeBones(this._profBonesScratch);
    netcodeProfiler.recordLocalBones(this._profBonesScratch);

    // Remote chars: pelvis position + input consumed this tick.
    if (args.remoteInputs) {
      for (const r of args.remoteInputs) {
        const remoteSnap = synth.characters.find((c) => c.id === r.charId);
        if (!remoteSnap) continue;
        netcodeProfiler.recordRemoteTick({
          charId: r.charId,
          clientTick: this.tickCounter,
          serverTickApprox: args.ackTickObserved,
          pelvisX: remoteSnap.pelvisX,
          pelvisY: remoteSnap.pelvisY,
          pelvisZ: remoteSnap.pelvisZ,
          facingYaw: remoteSnap.facingYaw,
          inputQueueDepthBefore: r.depthBefore,
          inputSource: r.source,
          inputConsumedTick: r.consumedTick,
          inputMoveX: r.frame?.moveX ?? 0,
          inputMoveZ: r.frame?.moveZ ?? 0,
          inputAimYaw: r.frame?.aimYaw ?? 0,
          inputButtons: r.frame?.buttons ?? 0
        });
      }
    }
  }
}

// -- helpers ---------------------------------------------------------------

interface Bracketed<T extends { receivedAtMs: number }> {
  a: T | null;
  b: T | null;
  /** Interpolation factor 0..1 between a and b. */
  t: number;
}
function bracket<T extends { receivedAtMs: number }>(buf: T[], timeMs: number): Bracketed<T> {
  if (buf.length === 0) return { a: null, b: null, t: 0 };
  if (buf.length === 1) return { a: buf[0]!, b: buf[0]!, t: 0 };
  for (let i = buf.length - 1; i > 0; i--) {
    const a = buf[i - 1]!;
    const b = buf[i]!;
    if (a.receivedAtMs <= timeMs && b.receivedAtMs >= timeMs) {
      const span = b.receivedAtMs - a.receivedAtMs;
      const t = span > 0 ? (timeMs - a.receivedAtMs) / span : 0;
      return { a, b, t: Math.max(0, Math.min(1, t)) };
    }
  }
  if (timeMs < buf[0]!.receivedAtMs) return { a: buf[0]!, b: buf[0]!, t: 0 };
  const last = buf[buf.length - 1]!;
  return { a: last, b: last, t: 1 };
}

function lerp(a: number, b: number, t: number): number { return a + (b - a) * t; }
function lerpAngle(a: number, b: number, t: number): number {
  let d = b - a;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d < -Math.PI) d += 2 * Math.PI;
  return a + d * t;
}

/** Interpolate pos (lerp) + rot (slerp) between two 13-floats-per-bone wire
 *  buffers. Velocities are ignored — `out` is the 7-floats-per-bone render
 *  format. */
function interpBonesFromWire(out: Float32Array, a: Float32Array, b: Float32Array, t: number, boneCount: number): void {
  for (let i = 0; i < boneCount; i++) {
    const oa = i * FLOATS_PER_BONE_WIRE;
    const ob = i * FLOATS_PER_BONE_WIRE;
    const oo = i * FLOATS_PER_BONE_RENDER;
    out[oo + 0] = a[oa + 0]! + (b[ob + 0]! - a[oa + 0]!) * t;
    out[oo + 1] = a[oa + 1]! + (b[ob + 1]! - a[oa + 1]!) * t;
    out[oo + 2] = a[oa + 2]! + (b[ob + 2]! - a[oa + 2]!) * t;
    slerpWire(out, oo + 3, a, oa + 3, b, ob + 3, t);
  }
}

/** Copy just pos+rot (first 7 floats per bone) from a 13-floats-per-bone
 *  wire buffer into the render-format output. */
function copyPosRotFromWire(out: Float32Array, src: Float32Array, boneCount: number): void {
  for (let i = 0; i < boneCount; i++) {
    const s = i * FLOATS_PER_BONE_WIRE;
    const d = i * FLOATS_PER_BONE_RENDER;
    out[d + 0] = src[s + 0]!;
    out[d + 1] = src[s + 1]!;
    out[d + 2] = src[s + 2]!;
    out[d + 3] = src[s + 3]!;
    out[d + 4] = src[s + 4]!;
    out[d + 5] = src[s + 5]!;
    out[d + 6] = src[s + 6]!;
  }
}

function slerpWire(out: Float32Array, oo: number, a: Float32Array, ao: number, b: Float32Array, bo: number, t: number): void {
  let ax = a[ao]!, ay = a[ao + 1]!, az = a[ao + 2]!, aw = a[ao + 3]!;
  let bx = b[bo]!, by = b[bo + 1]!, bz = b[bo + 2]!, bw = b[bo + 3]!;
  let dot = ax * bx + ay * by + az * bz + aw * bw;
  if (dot < 0) { bx = -bx; by = -by; bz = -bz; bw = -bw; dot = -dot; }
  let s0: number, s1: number;
  if (dot > 0.9995) {
    s0 = 1 - t; s1 = t;
  } else {
    const omega = Math.acos(dot);
    const sinOmega = Math.sin(omega);
    s0 = Math.sin((1 - t) * omega) / sinOmega;
    s1 = Math.sin(t * omega) / sinOmega;
  }
  let rx = ax * s0 + bx * s1;
  let ry = ay * s0 + by * s1;
  let rz = az * s0 + bz * s1;
  let rw = aw * s0 + bw * s1;
  const mag = Math.hypot(rx, ry, rz, rw) || 1;
  out[oo] = rx / mag;
  out[oo + 1] = ry / mag;
  out[oo + 2] = rz / mag;
  out[oo + 3] = rw / mag;
}

function readRosterFromState(state: GameStateLike, localCharId: number): TeamMember[] {
  const out: TeamMember[] = [];
  for (const p of state.players) {
    out.push({
      id: p.charId,
      name: p.name,
      color: p.color,
      hatId: (p.hatId || null) as HatId | null,
      team: p.team === 0 ? 'red' : 'blue',
      isLocal: p.charId === localCharId
    });
  }
  out.sort((a, b) => a.id - b.id);
  if (out.length !== 4) {
    throw new Error(`[ColyseusTransport] expected 4 player slots, got ${out.length}`);
  }
  return out;
}

function cloneSlot(p: PlayerSlotState): PlayerSlotState {
  return {
    charId: p.charId, sessionId: p.sessionId, isAI: p.isAI, connected: p.connected,
    name: p.name, color: p.color, hatId: p.hatId, team: p.team,
    hp: p.hp, ko: p.ko, koStunRemaining: p.koStunRemaining,
    wasted: p.wasted, ringedOut: p.ringedOut, knockedOut: p.knockedOut,
    stamina: p.stamina, staminaLocked: p.staminaLocked, stunRemaining: p.stunRemaining,
    facingYaw: p.facingYaw, pelvisX: p.pelvisX, pelvisY: p.pelvisY, pelvisZ: p.pelvisZ,
    swingPhaseL: p.swingPhaseL, swingPhaseR: p.swingPhaseR,
    grabbing: p.grabbing, doubleHolding: p.doubleHolding, beingGrabbed: p.beingGrabbed,
    wriggle: p.wriggle,
    bothAir: p.bothAir ?? false,
    isSwimming: p.isSwimming ?? false,
    ping: p.ping ?? 0,
    lastProcessedInputTick: p.lastProcessedInputTick ?? 0
  };
}

function copySlotInto(cs: CharacterSnapshot, p: PlayerSlotState): void {
  copyServerScalarsInto(cs, p);
  cs.facingYaw = p.facingYaw;
  cs.pelvisX = p.pelvisX;
  cs.pelvisY = p.pelvisY;
  cs.pelvisZ = p.pelvisZ;
  cs.swingPhaseL = p.swingPhaseL;
  cs.swingPhaseR = p.swingPhaseR;
  cs.grabbing = p.grabbing;
  cs.doubleHolding = p.doubleHolding;
  cs.bothAir = p.bothAir;
  cs.isSwimming = p.isSwimming ?? false;
}

/** Pose-free overlay used for the LOCAL character only. The local Rapier
 *  world has already produced an up-to-date pose via Character.writeSnapshot
 *  — overlaying the server's (ping/2 stale) pelvis/facing on top would
 *  drag the camera and nameplate behind real movement, which is exactly
 *  the stutter the player sees on a high-latency link. We still take
 *  HP/KO/stamina/stun/grab-victim state from the server because those are
 *  server-authoritative game state, not pose. Pair: copySlotInto, used for
 *  remote characters where the server IS the truth for pose too. */
function copyServerScalarsInto(cs: CharacterSnapshot, p: PlayerSlotState): void {
  cs.hp = p.hp;
  cs.ko = p.ko;
  cs.koStunRemaining = p.koStunRemaining;
  cs.wasted = p.wasted;
  cs.ringedOut = p.ringedOut;
  cs.knockedOut = p.knockedOut;
  cs.stamina = p.stamina;
  cs.staminaLocked = p.staminaLocked;
  cs.stunRemaining = p.stunRemaining;
  // beingGrabbed / wriggle reflect another character grabbing us — that
  // arbitration lives on the server, so even the local character takes
  // these from the slot.
  cs.beingGrabbed = p.beingGrabbed;
  cs.wriggle = p.wriggle;
}

function isDead(snap: SimState, id: number): boolean {
  const c = snap.characters.find((ch) => ch.id === id);
  if (!c) return true;
  return c.wasted || c.ringedOut;
}

void TICK_HZ;
